import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  User,
  CreditCard,
  FileText,
  Trash2,
  Building,
  ArrowRightLeft
} from 'lucide-react';
import { processedTransactionApi } from '@/lib/api/ProcessedTransactionApi';
import { toast } from 'sonner';

const ProcessedTransactionDetailPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: transactionDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['processedTransaction', transactionId],
    queryFn: () => processedTransactionApi.getProcessedTransactionDetails(transactionId!),
    enabled: !!transactionId,
  });

  const {
    data: timedPullList,
    isLoading: isTimedPullsLoading,
  } = useQuery({
    queryKey: ['timedPulls', transactionDetails?.TransactionId],
    queryFn: () => processedTransactionApi.adminListTimedPulls(100, 0),
    enabled: !!transactionDetails,
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: processedTransactionApi.deleteProcessedTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processedTransactions'] });
      navigate('/processed-transactions');
    },
  });

  const [tpAmount, setTpAmount] = useState<string>('');
  const [tpSource, setTpSource] = useState<string>('');
  const [tpRecipient, setTpRecipient] = useState<string>('');
  const [tpInvoice, setTpInvoice] = useState<string>('');
  const [tpComment1, setTpComment1] = useState<string>('');
  const [tpComment2, setTpComment2] = useState<string>('');
  const [tpCertStr, setTpCertStr] = useState<string>('');
  const [tpTermId, setTpTermId] = useState<string>('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState<boolean>(false);
  const [selectedTimedPullId, setSelectedTimedPullId] = useState<number | null>(null);

  const createTimedPullMutation = useMutation({
    mutationFn: async () => {
      const transId = transactionDetails!.TransactionId;
      const amountNum = Number(tpAmount);
      const sourceNum = Number(tpSource);
      const recipientNum = Number(tpRecipient);
      if (!amountNum || amountNum <= 0) throw new Error('Amount must be greater than zero');
      if (!sourceNum || sourceNum <= 0) throw new Error('Source account must be greater than zero');
      if (!recipientNum || recipientNum <= 0) throw new Error('Recipient account must be greater than zero');
      const request = {
        accountNum: sourceNum,
        recAccntNum: recipientNum,
        amount: amountNum,
        transNum: Number(transId),
        invNum: tpInvoice || undefined,
        comment1: tpComment1 || undefined,
        comment2: tpComment2 || undefined,
      };
      return processedTransactionApi.createTimedPull(request, tpCertStr, tpTermId);
    },
    onSuccess: () => {
      toast.success('Timed Pull created');
      queryClient.invalidateQueries({ queryKey: ['timedPulls'] });
      setIsCreateDialogOpen(false);
      setTpAmount('');
      setTpInvoice('');
      setTpComment1('');
      setTpComment2('');
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Failed to create Timed Pull';
      toast.error(msg);
    },
  });

  const {
    data: timedPullDetails,
    isLoading: isTimedPullDetailsLoading,
    error: timedPullDetailsError,
  } = useQuery({
    queryKey: ['timedPullDetails', selectedTimedPullId],
    queryFn: () => processedTransactionApi.adminGetTimedPullDetails(String(selectedTimedPullId)),
    enabled: !!selectedTimedPullId && isDetailsDialogOpen,
  });

  const handleDeleteTransaction = () => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransactionMutation.mutate(transactionId!);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatAmount = (amount: number, currencyCode: string) => {
    // Convert from minor units (cents) to decimal (dollars)
    const amountInDecimal = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amountInDecimal);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading transaction details...</div>
        </div>
      </div>
    );
  }

  if (error || !transactionDetails) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">
            Error loading transaction details: {(error as Error)?.message || 'Transaction not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/processed-transactions">
          <Button variant="outline" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Transactions
          </Button>
        </Link>

        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                Transaction Details
              </h1>
            </div>
            <p className="text-gray-600">
              Transaction ID: {transactionDetails.TransactionId}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Badge className={getStatusColor(transactionDetails.Status)}>
              {transactionDetails.Status}
            </Badge>
            {transactionDetails.IsReversed && (
              <Badge className="bg-orange-100 text-orange-800">
                Reversed
              </Badge>
            )}
            <Button
              variant="destructive"
              onClick={handleDeleteTransaction}
              disabled={deleteTransactionMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deleteTransactionMutation.isPending ? 'Deleting...' : 'Delete Transaction'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Transaction Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Amount:</span>
                <span className="font-medium">
                  {formatAmount(transactionDetails.Amount, transactionDetails.CurrencyCode)}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Payer Account ID:</span>
                <span className="font-medium">{transactionDetails.PayerAccountId}</span>
              </div>

              <div className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Payment Method ID:</span>
                <span className="font-medium">{transactionDetails.PaymentMethodId}</span>
              </div>

              {transactionDetails.MerchantProfileId && (
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Merchant Profile ID:</span>
                  <span className="font-medium">{transactionDetails.MerchantProfileId}</span>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Created:</span>
                <span className="font-medium">{formatDate(transactionDetails.CreatedDate)}</span>
              </div>

              {transactionDetails.LastModifiedDate && (
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Last Modified:</span>
                  <span className="font-medium">{formatDate(transactionDetails.LastModifiedDate)}</span>
                </div>
              )}

              {transactionDetails.TransactionHistoryId && (
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Transaction History ID:</span>
                  <span className="font-medium">{transactionDetails.TransactionHistoryId}</span>
                </div>
              )}

              {transactionDetails.TransactionDetails?.AuthorizationCode && (
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Authorization Code:</span>
                  <span className="font-medium">{transactionDetails.TransactionDetails.AuthorizationCode}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        {(transactionDetails.Invoice || transactionDetails.Comment1 || transactionDetails.Comment2 || transactionDetails.IsDebtRepayment !== undefined) && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Additional Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {transactionDetails.Invoice && (
                  <div>
                    <span className="text-sm text-gray-600">Invoice:</span>
                    <p className="font-medium">{transactionDetails.Invoice}</p>
                  </div>
                )}

                {transactionDetails.Comment1 && (
                  <div>
                    <span className="text-sm text-gray-600">Comment 1:</span>
                    <p className="font-medium">{transactionDetails.Comment1}</p>
                  </div>
                )}

                {transactionDetails.Comment2 && (
                  <div>
                    <span className="text-sm text-gray-600">Comment 2:</span>
                    <p className="font-medium">{transactionDetails.Comment2}</p>
                  </div>
                )}

                {transactionDetails.IsDebtRepayment !== undefined && (
                  <div>
                    <span className="text-sm text-gray-600">Is Debt Repayment:</span>
                    <p className="font-medium">{transactionDetails.IsDebtRepayment ? 'Yes' : 'No'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reversal Information */}
        {(transactionDetails.IsReversed || transactionDetails.ReverseTransactionId) && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ArrowRightLeft className="h-5 w-5" />
                <span>Reversal Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {transactionDetails.IsReversed && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-orange-900 mb-2">This transaction has been reversed</h3>
                      {transactionDetails.ReversedAt && (
                        <div className="flex items-center space-x-2 text-sm text-orange-800 mb-2">
                          <Calendar className="h-4 w-4" />
                          <span>Reversed on: {formatDate(transactionDetails.ReversedAt)}</span>
                        </div>
                      )}
                      {transactionDetails.ReverseTransactionId && (
                        <div className="mt-3">
                          <Link
                            to={`/processed-transactions/${transactionDetails.ReverseTransactionId}`}
                            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <ArrowRightLeft className="h-4 w-4 mr-1" />
                            View Reversal Transaction (ID: {transactionDetails.ReverseTransactionId})
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!transactionDetails.IsReversed && transactionDetails.ReverseTransactionId && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 mb-2">This is a reversal transaction</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        This transaction reverses a previous transaction. It typically has negative amounts to offset the original transaction.
                      </p>
                      <div className="mt-3">
                        <Link
                          to={`/processed-transactions/${transactionDetails.ReverseTransactionId}`}
                          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <ArrowRightLeft className="h-4 w-4 mr-1" />
                          View Original Transaction (ID: {transactionDetails.ReverseTransactionId})
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center space-x-2">
              <ArrowRightLeft className="h-5 w-5" />
              <span>Timed Pulls</span>
            </CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>Create Timed Pull</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Timed Pull</DialogTitle>
                  <DialogDescription>
                    Enter the details to create a timed pull for this transaction.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Amount (cents)</Label>
                    <Input
                      value={tpAmount}
                      onChange={(e) => setTpAmount(e.target.value)}
                      placeholder="100"
                      type="number"
                    />
                  </div>
                  <div>
                    <Label>Source Account</Label>
                    <Input
                      value={tpSource}
                      onChange={(e) => setTpSource(e.target.value)}
                      placeholder="Source account number"
                      type="number"
                    />
                  </div>
                  <div>
                    <Label>Recipient Account</Label>
                    <Input
                      value={tpRecipient}
                      onChange={(e) => setTpRecipient(e.target.value)}
                      placeholder="Recipient account number"
                      type="number"
                    />
                  </div>
                  <div>
                    <Label>Invoice</Label>
                    <Input value={tpInvoice} onChange={(e) => setTpInvoice(e.target.value)} placeholder="INV-001" />
                  </div>
                  <div>
                    <Label>Comment 1</Label>
                    <Input value={tpComment1} onChange={(e) => setTpComment1(e.target.value)} />
                  </div>
                  <div>
                    <Label>Comment 2</Label>
                    <Input value={tpComment2} onChange={(e) => setTpComment2(e.target.value)} />
                  </div>
                  <div>
                    <Label>Cert String</Label>
                    <Input value={tpCertStr} onChange={(e) => setTpCertStr(e.target.value)} />
                  </div>
                  <div>
                    <Label>Term ID</Label>
                    <Input value={tpTermId} onChange={(e) => setTpTermId(e.target.value)} />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    onClick={() => createTimedPullMutation.mutate()}
                    disabled={createTimedPullMutation.isPending}
                  >
                    {createTimedPullMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              {isTimedPullsLoading ? (
                <div className="text-sm text-gray-600">Loading timed pulls...</div>
              ) : (
                <div className="space-y-3">
                  {(
                    timedPullList?.TimedPulls.filter(
                      (tp) => tp.TransactionId === transactionDetails.TransactionId
                    ) || []
                  ).length === 0 ? (
                    <div className="text-sm text-gray-600">No timed pulls for this transaction.</div>
                  ) : (
                    <div className="space-y-3">
                      {(timedPullList?.TimedPulls.filter(
                        (tp) => tp.TransactionId === transactionDetails.TransactionId
                      ) || []).map((tp) => (
                        <div key={tp.TimedPullId} className="flex items-center justify-between border rounded-md p-3">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <Badge className={getStatusColor(tp.Status)}>{tp.Status}</Badge>
                              <span className="text-sm text-gray-600">ID: {tp.TimedPullId}</span>
                            </div>
                            <div className="text-sm">
                              {formatAmount(tp.Amount, transactionDetails.CurrencyCode)} to {tp.RecipientAccountNum}
                            </div>
                            <div className="text-xs text-gray-500">Created: {formatDate(tp.CreatedDate)}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setSelectedTimedPullId(tp.TimedPullId);
                                setIsDetailsDialogOpen(true);
                              }}
                            >
                              View Details
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>


          </CardContent>
        </Card>

        <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Timed Pull Details</DialogTitle>
              <DialogDescription>
                Detailed information for timed pull ID {selectedTimedPullId ?? ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {isTimedPullDetailsLoading ? (
                <div className="text-sm text-gray-600">Loading...</div>
              ) : timedPullDetailsError ? (
                <div className="text-sm text-red-600">Failed to load details</div>
              ) : timedPullDetails ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Badge className={getStatusColor(timedPullDetails.Status)}>{timedPullDetails.Status}</Badge>
                    <span className="text-sm text-gray-600">ID: {timedPullDetails.TimedPullId}</span>
                  </div>
                  <div className="text-sm">
                    {formatAmount(timedPullDetails.Amount, transactionDetails.CurrencyCode)} to {timedPullDetails.RecipientAccountNum}
                  </div>
                  <div className="text-xs text-gray-500">Created: {formatDate(timedPullDetails.CreatedDate)}</div>
                  {timedPullDetails.CompletedDate && (
                    <div className="text-xs text-gray-500">Completed: {formatDate(timedPullDetails.CompletedDate)}</div>
                  )}
                  <div className="text-sm">Source Account: {timedPullDetails.SourceAccountNum}</div>
                  <div className="text-sm">Recipient Account: {timedPullDetails.RecipientAccountNum}</div>
                  {timedPullDetails.Invoice && <div className="text-sm">Invoice: {timedPullDetails.Invoice}</div>}
                  {timedPullDetails.Comment1 && <div className="text-sm">Comment 1: {timedPullDetails.Comment1}</div>}
                  {timedPullDetails.Comment2 && <div className="text-sm">Comment 2: {timedPullDetails.Comment2}</div>}
                </div>
              ) : (
                <div className="text-sm text-gray-600">No details available.</div>
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ProcessedTransactionDetailPage;
